// src/pages/context/ContextAdminPage.tsx
// Context resolution inspector — resolve the Context Panel layout for any
// entity, browse the manifest registry, and edit tenant/user configuration.
// See docs/ux/context-ia.md. Admin-gated (AdminRoute, in App.tsx).

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { LayoutResolver } from '@/features/context/LayoutResolver';
import { ManifestsPanel } from '@/features/context/ManifestsPanel';
import { TenantConfigPanel } from '@/features/context/TenantConfigPanel';
import { UserPreferencesPanel } from '@/features/context/UserPreferencesPanel';

type Tab = 'layout' | 'manifests' | 'tenant' | 'preferences';
const TABS: Tab[] = ['layout', 'manifests', 'tenant', 'preferences'];

export const ContextAdminPage = observer(() => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'layout';
  }, [searchParams]);

  return (
    <div className="space-y-4 p-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('context.title')}</h1>

      <div role="tablist" aria-label={t('context.title')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`context.tab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'layout' && <LayoutResolver />}
      {tab === 'manifests' && <ManifestsPanel />}
      {tab === 'tenant' && <TenantConfigPanel />}
      {tab === 'preferences' && <UserPreferencesPanel />}
    </div>
  );
});

export default ContextAdminPage;
